import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import * as recruitmentService from '../../services/recruitmentService';

interface OfferGeneratorProps {
  applicationId: string;
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  jobData?: any;
  onClose: () => void;
  onSuccess: () => void;
}

const OfferGenerator: React.FC<OfferGeneratorProps> = ({
  applicationId,
  candidateName,
  candidateEmail,
  jobTitle,
  jobData,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    job_title: jobTitle,
    department: jobData?.department || 'Engineering',
    location: jobData?.location || 'London, UK',
    employment_type: jobData?.employment_type || 'full-time',
    start_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    salary: jobData?.salary_max || 70000,
    salary_currency: jobData?.salary_currency || 'GBP',
    bonus: '',
    equity: '',
    benefits: jobData?.benefits || [
      'Health insurance',
      'Pension contribution (5%)',
      '25 days holiday + bank holidays',
      'Remote work flexibility',
      'Learning & development budget'
    ],
    probation_period: 3,
    notice_period: 1,
    offer_valid_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    additional_terms: '',
    send_email: true,
  });

  const [generating, setGenerating] = useState(false);
  const [currentBenefit, setCurrentBenefit] = useState('');

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addBenefit = () => {
    if (!currentBenefit.trim()) return;
    handleChange('benefits', [...formData.benefits, currentBenefit.trim()]);
    setCurrentBenefit('');
  };

  const removeBenefit = (index: number) => {
    handleChange('benefits', formData.benefits.filter((_: string, i: number) => i !== index));
  };

  const generateOffer = async () => {
    try {
      setGenerating(true);

      // Validation
      if (!formData.start_date) {
        toast.error('Start date is required');
        return;
      }

      if (!formData.salary) {
        toast.error('Salary is required');
        return;
      }

      // Create offer
      const offer = await recruitmentService.createOffer({
        application_id: applicationId,
        job_id: jobData?.id,
        job_title: formData.job_title,
        department: formData.department,
        employment_type: formData.employment_type as any,
        start_date: formData.start_date,
        salary: formData.salary,
        salary_currency: formData.salary_currency,
        bonus: formData.bonus ? parseFloat(formData.bonus) : undefined,
        equity: formData.equity || undefined,
        benefits: formData.benefits,
        probation_months: formData.probation_period,
        expires_at: formData.offer_valid_until,
      });

      // Optionally send email
      if (formData.send_email && offer.id) {
        await recruitmentService.sendOffer(offer.id);
        toast.success('Offer generated and sent!');
      } else {
        toast.success('Offer generated successfully!');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to generate offer:', error);
      toast.error(error.message || 'Failed to generate offer');
    } finally {
      setGenerating(false);
    }
  };

  const getSalaryDisplay = () => {
    const symbol = formData.salary_currency === 'GBP' ? '£' : formData.salary_currency === 'USD' ? '$' : '€';
    return `${symbol}${formData.salary?.toLocaleString() || 0}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Generate Offer Letter</h2>
            <p className="text-sm text-gray-500 mt-1">
              For: {candidateName} • {jobTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Position Details */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Position Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                <input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                <select
                  value={formData.employment_type}
                  onChange={(e) => handleChange('employment_type', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                </select>
              </div>
            </div>
          </section>

          {/* Start Date & Validity */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Offer Valid Until
                </label>
                <input
                  type="date"
                  value={formData.offer_valid_until}
                  onChange={(e) => handleChange('offer_valid_until', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Compensation */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Compensation</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Annual Salary <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.salary_currency}
                    onChange={(e) => handleChange('salary_currency', e.target.value)}
                    className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                  <input
                    type="number"
                    value={formData.salary}
                    onChange={(e) => handleChange('salary', parseInt(e.target.value))}
                    placeholder="70000"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">{getSalaryDisplay()} per year</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bonus</label>
                <input
                  type="text"
                  value={formData.bonus}
                  onChange={(e) => handleChange('bonus', e.target.value)}
                  placeholder="e.g., 10% annual"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Equity/Stock Options</label>
              <input
                type="text"
                value={formData.equity}
                onChange={(e) => handleChange('equity', e.target.value)}
                placeholder="e.g., 0.5% company stock options"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </section>

          {/* Benefits */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits & Perks</h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentBenefit}
                  onChange={(e) => setCurrentBenefit(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                  placeholder="Add a benefit..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={addBenefit}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.benefits.map((benefit: string, index: number) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="flex-1 text-sm text-gray-700">✓ {benefit}</span>
                    <button
                      type="button"
                      onClick={() => removeBenefit(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Terms */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms & Conditions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Probation Period (months)</label>
                <input
                  type="number"
                  value={formData.probation_period}
                  onChange={(e) => handleChange('probation_period', parseInt(e.target.value))}
                  min="0"
                  max="12"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notice Period (months)</label>
                <input
                  type="number"
                  value={formData.notice_period}
                  onChange={(e) => handleChange('notice_period', parseInt(e.target.value))}
                  min="0"
                  max="12"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Terms</label>
              <textarea
                value={formData.additional_terms}
                onChange={(e) => handleChange('additional_terms', e.target.value)}
                placeholder="Any additional terms, conditions, or notes..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </section>

          {/* Options */}
          <section className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="send_email"
                checked={formData.send_email}
                onChange={(e) => handleChange('send_email', e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label htmlFor="send_email" className="text-sm text-gray-700">
                Send offer letter to candidate via email
              </label>
            </div>
            {formData.send_email && (
              <p className="text-xs text-gray-500 mt-2 ml-7">
                Offer will be sent to: {candidateEmail}
              </p>
            )}
          </section>

          {/* Preview */}
          <section className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Offer Summary</h3>
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">Position:</span> {formData.job_title}</p>
              <p><span className="font-medium">Department:</span> {formData.department}</p>
              <p><span className="font-medium">Location:</span> {formData.location}</p>
              <p><span className="font-medium">Salary:</span> {getSalaryDisplay()} per year</p>
              {formData.bonus && <p><span className="font-medium">Bonus:</span> {formData.bonus}</p>}
              {formData.equity && <p><span className="font-medium">Equity:</span> {formData.equity}</p>}
              <p><span className="font-medium">Start Date:</span> {new Date(formData.start_date).toLocaleDateString()}</p>
              <p><span className="font-medium">Benefits:</span> {formData.benefits.length} items</p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            disabled={generating}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={generateOffer}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={generating}
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <span>📄</span>
                <span>Generate Offer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferGenerator;
